import { useRef, useEffect, useCallback } from 'react';
import { useStores } from '@/contexts/StoreContext';
import {
  getRelativeMousePosition,
  screenToCanvas,
  canvasCoordinatesToPoint,
} from '@/utils/geometry/coordinates';

const DOUBLE_CLICK_TIMEOUT = 500; // ms

export const useCanvasInteraction = (
  svgRef: React.RefObject<SVGSVGElement>
) => {
  const { projectStore, uiStore, toolManager } = useStores();
  const interactionState = useRef({
    isPanning: false,
    panStartPoint: { x: 0, y: 0 },
    lastClickTime: 0,
    lastClickPoint: { x: 0, y: 0 },
  }).current;

  const getToolContext = (event: PointerEvent) => {
    if (!svgRef.current) throw new Error('SVG ref is not available');
    const point = getRelativeMousePosition(event, svgRef.current);
    const { canvasOffset, zoom } = uiStore;
    const canvasCoords = screenToCanvas(point.x, point.y, {
      canvasOffset,
      zoom,
    });
    const canvasPoint = canvasCoordinatesToPoint(canvasCoords);
    return {
      rootStore: {
        projectStore,
        uiStore,
        toolManager,
        historyStore: projectStore.historyStore,
      },
      startPoint: uiStore.startPoint || canvasPoint,
      currentPoint: canvasPoint,
      isDragging: uiStore.isDrawing,
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      elements: projectStore.elements,
      persistentStyle: uiStore.persistentStyle,
    };
  };

  const handlePointerDown = useCallback(
    (event: PointerEvent) => {
      if (!svgRef.current) return;
      svgRef.current.setPointerCapture(event.pointerId);

      const point = getRelativeMousePosition(event, svgRef.current);

      if (uiStore.spacebarPressed || event.button === 1) {
        interactionState.isPanning = true;
        interactionState.panStartPoint = point;
        uiStore.setIsPanning(true);
        return;
      }

      const toolContext = getToolContext(event);
      uiStore.startDrawing(toolContext.currentPoint);
      const preview = toolManager.onStart(toolContext);
      if (preview) {
        uiStore.setPreviewElement(preview);
      }
    },
    [toolManager, projectStore, uiStore, interactionState]
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      if (!svgRef.current) return;

      if (interactionState.isPanning) {
        const currentPoint = getRelativeMousePosition(event, svgRef.current!);
        const deltaX = currentPoint.x - interactionState.panStartPoint.x;
        const deltaY = currentPoint.y - interactionState.panStartPoint.y;
        uiStore.panBy({ deltaX, deltaY });
        interactionState.panStartPoint = currentPoint;
        return;
      }

      const toolContext = getToolContext(event);
      svgRef.current.style.cursor = toolManager.getCursor(toolContext);

      if (!uiStore.isDrawing) return;

      const preview = toolManager.onMove(toolContext);
      if (preview !== undefined) {
        uiStore.setPreviewElement(preview);
      }
    },
    [toolManager, uiStore, interactionState]
  );

  const handlePointerUp = useCallback(
    (event: PointerEvent) => {
      if (!svgRef.current) return;
      svgRef.current.releasePointerCapture(event.pointerId);

      if (interactionState.isPanning) {
        interactionState.isPanning = false;
        uiStore.setIsPanning(false);
        return;
      }

      if (uiStore.isDrawing) {
        const toolContext = getToolContext(event);

        const now = Date.now();
        const timeSinceLastClick = now - interactionState.lastClickTime;
        const dist = Math.hypot(
          toolContext.currentPoint.x - interactionState.lastClickPoint.x,
          toolContext.currentPoint.y - interactionState.lastClickPoint.y
        );

        if (timeSinceLastClick < DOUBLE_CLICK_TIMEOUT && dist < 5) {
          toolManager.onDoubleClick?.(toolContext);
          interactionState.lastClickTime = 0; // Reset to prevent triple-click
        } else {
          toolManager.onEnd(toolContext);
          interactionState.lastClickTime = now;
          interactionState.lastClickPoint = toolContext.currentPoint;
        }

        uiStore.endDrawing();
        uiStore.setPreviewElement(null);
      }
    },
    [toolManager, uiStore, interactionState]
  );

  useEffect(() => {
    const svgElement = svgRef.current;
    if (!svgElement) return;

    svgElement.addEventListener('pointerdown', handlePointerDown);
    svgElement.addEventListener('pointermove', handlePointerMove);
    svgElement.addEventListener('pointerup', handlePointerUp);

    return () => {
      svgElement.removeEventListener('pointerdown', handlePointerDown);
      svgElement.removeEventListener('pointermove', handlePointerMove);
      svgElement.removeEventListener('pointerup', handlePointerUp);
    };
  }, [svgRef, handlePointerDown, handlePointerMove, handlePointerUp]);

  useEffect(() => {
    if (!svgRef.current) return;
    if (uiStore.spacebarPressed && !interactionState.isPanning) {
      svgRef.current.style.cursor = 'grab';
    } else if (!uiStore.spacebarPressed && !interactionState.isPanning) {
      svgRef.current.style.cursor = 'default';
    }
  }, [uiStore.spacebarPressed, interactionState.isPanning]);
};
